import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useI18n } from '../../../i18n/I18nProvider';
import './ToastContext.css';

const ToastContext = createContext(null);

export function ToastProvider({ children }: any) {
  const { t } = useI18n();
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // action: optional { label, onClick } -- an Undo, say. Clicking it also closes the toast.
  const push = useCallback((message, type = 'info', duration = 3200, action = null) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((prev) => [...prev, { id, message, type, action }]);

    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, duration);
  }, []);

  const api = useMemo(() => ({
    info: (message, duration) => push(message, 'info', duration),
    success: (message, duration) => push(message, 'success', duration),
    warning: (message, duration) => push(message, 'warning', duration),
    error: (message, duration) => push(message, 'error', duration),
    /** A toast with a button: show({ message, type, duration, action: { label, onClick } }). */
    show: ({ message, type = 'info', duration = 6000, action = null }) => push(message, type, duration, action),
  }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-container" role="status" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <span className="toast-message">{toast.message}</span>
            {toast.action && (
              <button
                type="button"
                className="toast-action"
                onClick={() => { toast.action.onClick(); dismiss(toast.id); }}
              >
                {toast.action.label}
              </button>
            )}
            <button
              type="button"
              className="toast-close"
              onClick={() => dismiss(toast.id)}
              aria-label={t('toast.dismissNotification')}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }

  return context;
}
